<template>
  <div class="files-list">
    <ul>
      <li
        v-for="file in files"
        :key="file.public_id"
        class="files-list__item"
        :class="{ 'file-deleted': isFileDeleted(file) }"
      >
        <div class="files-list__info">
          <span :class="statusClass(file)">
            {{ file.name }} ({{ formatBytes(file.size) }})
          </span>
          <div class="files-list__actions flex align-center">
            <template v-if="!isFileDeleted(file)">
              <a
                :href="getFileDownloadUrl(file)"
                target="_blank"
                class="download-link mr-2"
              >
                <DownloadIcon color="#007bff" />
              </a>
              <button
                v-if="showDeleteFile"
                class="remove-file-button"
                @click.stop="markFileAsDeleted(file)"
              >
                <TrashIcon color="#DDE2E5" />
              </button>
            </template>
            <template v-else>
              <span class="text-deleted mr-2">Буде видалено</span>
              <button @click.stop="unmarkFileAsDeleted(file)" class="remove-file-button">
                <RevertIcon color="#DDE2E5" />
              </button>
            </template>
          </div>
        </div>
        <div class="files-list__tooltip">
          <div class="files-list__tooltip-content">
            <template v-if="isImageFile(file.type)">
              <img
                :src="getFileDownloadUrl(file)"
                :alt="`Перегляд файлу ${file.name}`"
                class="preview-thumbnail"
              />
            </template>
            <template v-else>
              <p><strong>Назва:</strong> {{ file.name }}</p>
              <p><strong>Розмір:</strong> {{ formatBytes(file.size) }}</p>
              <p><strong>Тип:</strong> {{ file.type || 'Не визначено' }}</p>
            </template>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { fileUtils } from '@/shared/ui/file-upload';
import { DownloadIcon, RevertIcon, TrashIcon } from '@/shared/ui/icons';

export default {
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    showDeleteFile: {
      type: Boolean,
      default: false,
    },
    downloadLinkGenerator: {
      type: Function,
      default: () => { return ''; },
    },
  },
  mixins: [ fileUtils ],
  components: { DownloadIcon, TrashIcon, RevertIcon },
  data() {
    return {
      deletedFileIds: new Set(),
    };
  },
  computed: {
    getMarkedForDeleteFiles() {
      return this.files.filter(file => this.deletedFileIds.has(file.public_id));
    },
  },
  methods: {
    getFileDownloadUrl(file) {
      return this.downloadLinkGenerator(file);
    },
    markFileAsDeleted(fileToMark) {
      if (fileToMark && fileToMark.public_id) {
        const newSet = new Set(this.deletedFileIds);
        newSet.add(fileToMark.public_id);
        this.deletedFileIds = newSet;
      }
    },
    isFileDeleted(file) {
      return this.deletedFileIds.has(file.public_id);
    },
    statusClass(file) {
      return {
        'text-deleted': this.isFileDeleted(file),
      };
    },
    unmarkFileAsDeleted(fileToUnmark) {
      if (fileToUnmark && fileToUnmark.public_id) {
        const newSet = new Set(this.deletedFileIds);
        newSet.delete(fileToUnmark.public_id);
        this.deletedFileIds = newSet;
      }
    },
    clearMarkedDeletedFiles() {
      this.deletedFileIds = new Set();
    },
  },
};
</script>

<style lang="scss" scoped>
.files-list {
  width: 100%;
  padding: 0 15px;
  text-align: left;
  border: 1px dashed #FB923C;
  border-radius: 10px;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__item {
    position: relative;
    padding: 8px 0;
    border-bottom: 1px dashed #F5F5F5;
    color: #09090B;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    &:last-child {
      border-bottom: none;
    }

    &:hover .files-list__tooltip {
      visibility: visible;
      opacity: 1;
    }
  }

  &__info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  }

  &__tooltip {
    position: absolute;
    visibility: hidden;
    opacity: 0;
    width: 200px;
    background-color: #FFFFFF;
    color: #09090B;
    text-align: center;
    border-radius: 6px;
    padding: 4px;
    bottom: 125%;
    left: 50%;
    margin-left: -75px;
    pointer-events: none;
    z-index: 1000;
    transition: opacity 0.3s, visibility 0.3s;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: #FFFFFF transparent transparent transparent;
    }
  }

  &__tooltip-content {
    text-align: left;
    font-size: 12px;

    p {
      margin: 0;
      line-height: 1.4;
    }
  }
}

.preview-thumbnail {
  max-width: 100%;
  height: auto;
  display: block;
  margin-bottom: 5px;
  border-radius: 4px;
}

.download-link {
  color: inherit;
  text-decoration: none;
  display: flex;
  align-items: center;
  &:hover {
    color: #FB923C;
  }
}

.text-deleted {
  color: #DDE2E5;
  font-style: italic;
}
</style>
